import {Button, Dropdown, DropdownDivider, DropdownItem} from 'platform/components';
import {Box, CSSDimension, getSize, Show, useAvailableHeight} from 'platform/foundation';

import {Fragment} from 'react';

import {isNotNil} from 'ramda-adjunct';

import {suffixTestId, TestIdProps} from 'shared/web';

import type {PageSectionNavigation} from '../../Page/Page';

// Window height below the top of the switcher that the open list cannot use: the switcher button
// (8 steps), the gap above the list (1), the list border and padding (5) and a margin at the bottom
// of the window (2).
const LIST_OFFSET_STEPS = 16;

interface SectionSwitcherProps extends TestIdProps {
  sectionNavigation: PageSectionNavigation;
}

/** Replaces the left section menu while the record header is collapsed. */
export function SectionSwitcher(props: SectionSwitcherProps) {
  // The list is as tall as the window allows: every section is visible when the window is tall
  // enough, and only a short window scrolls the list.
  const {availableHeightRef, availableHeight} = useAvailableHeight<HTMLDivElement>();
  const listMaxHeight: CSSDimension | undefined =
    availableHeight > LIST_OFFSET_STEPS
      ? `calc(${getSize(availableHeight)} - ${getSize(LIST_OFFSET_STEPS)})`
      : undefined;

  const activeItem = props.sectionNavigation.items.find(
    (item) => item.id === props.sectionNavigation.activeItemId
  );

  return (
    <Box ref={availableHeightRef}>
      <Dropdown
        dropdownControl={
          <Button
            variant="secondary"
            leftIcon="navigation/menu"
            rightIcon="navigation/expand_more"
            title={activeItem?.label}
            data-testid={suffixTestId('sectionSwitcher', props)}
          />
        }
        data-testid={suffixTestId('sectionSwitcher', props)}
      >
        <Box maxHeight={listMaxHeight} overflowY="auto">
          {props.sectionNavigation.items.map((item) => (
            <Fragment key={item.id}>
              <DropdownItem
                label={
                  isNotNil(item.badge?.value) ? `${item.label} (${item.badge.value})` : item.label
                }
                isActive={item.id === props.sectionNavigation.activeItemId}
                isDisabled={'isDisabled' in item && item.isDisabled}
                onClick={() => props.sectionNavigation.onItemChange(item.id)}
                data-testid={suffixTestId(`sectionSwitcher-${item.id}`, props)}
              />
              {/* The same groups as in the left section menu. */}
              <Show when={item.hasSeparator}>
                <DropdownDivider />
              </Show>
            </Fragment>
          ))}
        </Box>
      </Dropdown>
    </Box>
  );
}
